import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useCompanies, useDashboard } from '../api/hooks'
import { Empty, GradeBadge, Stat, StatusBadge, fmtDate, gradeColor } from '../components/ui'

// Categorical slots from the validated reference palette (blue, orange, aqua, yellow) — fixed order, never cycled.
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100']

export default function DashboardPage() {
  const { t } = useTranslation()
  const [companyId, setCompanyId] = useState<string>('')
  const dash = useDashboard(companyId || undefined)
  const companies = useCompanies()

  const trend = useMemo(() => {
    const d = dash.data?.scoreTrend ?? []
    const names = [...new Set(d.map((p) => p.companyName))].slice(0, 4)
    const byDate = new Map<string, Record<string, number | string>>()
    for (const p of d) {
      if (!names.includes(p.companyName)) continue
      const row = byDate.get(p.date) ?? { date: fmtDate(p.date) }
      row[p.companyName] = p.percent
      byDate.set(p.date, row)
    }
    return { names, rows: [...byDate.values()] }
  }, [dash.data])

  if (dash.isLoading) return <p>{t('common.loading')}</p>
  if (dash.isError || !dash.data) return <p className="error">{t('common.loadFailed')}</p>
  const d = dash.data

  return (
    <div className="stack">
      <div className="row between wrap">
        <h2>{t('dashboard.title')}</h2>
        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} aria-label={t('companies.title')}>
          <option value="">{t('common.allCompanies')}</option>
          {companies.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="grid stats">
        <Stat label={t('dashboard.openActions')} value={d.openActions} tone={d.openActions > 0 ? 'warn' : 'good'} />
        <Stat label={t('dashboard.overdueActions')} value={d.overdueActions} tone={d.overdueActions > 0 ? 'bad' : 'good'} />
        <Stat label={t('dashboard.dueSoon')} value={d.dueSoonActions} hint={t('dashboard.next7days')} />
        <Stat label={t('dashboard.drafts')} value={d.draftReports} />
        <Stat label={t('dashboard.completedThisMonth')} value={d.completedThisMonth} hint={t('dashboard.totalReports', { count: d.totalReports })} />
      </div>

      <div className="grid two">
        <section className="card">
          <h3>{t('dashboard.scoreTrend')}</h3>
          {trend.rows.length === 0 ? <Empty text={t('dashboard.noScoredReports')} /> : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trend.rows} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="var(--border)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: 'var(--muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fill: 'var(--muted)', fontSize: 12 }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} />
                {trend.names.length > 1 ? <Legend /> : null}
                {trend.names.map((n, i) => <Line key={n} type="monotone" dataKey={n} stroke={SERIES[i]} strokeWidth={2} dot={{ r: 4 }} connectNulls />)}
              </LineChart>
            </ResponsiveContainer>
          )}
        </section>

        <section className="card">
          <h3>{t('dashboard.gradeDistribution')}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.gradeDistribution} margin={{ top: 8, right: 16, left: -16, bottom: 0 }} barCategoryGap="30%">
              <CartesianGrid stroke="var(--border)" vertical={false} />
              <XAxis dataKey="grade" tick={{ fill: 'var(--text)', fontSize: 13 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: 'var(--muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} cursor={{ fill: 'var(--border)' }} />
              <Bar dataKey="count" name={t('dashboard.reports')} radius={[4, 4, 0, 0]} fill="#2a78d6" label={{ position: 'top', fill: 'var(--muted)', fontSize: 12 }}
                // grades are status-like: colour + letter label together, never colour alone
                shape={(props: unknown) => {
                  const p = props as { x: number; y: number; width: number; height: number; payload: { grade: string } }
                  return <rect x={p.x} y={p.y} width={p.width} height={p.height} rx={4} fill={gradeColor[p.payload.grade] ?? '#2a78d6'} />
                }} />
            </BarChart>
          </ResponsiveContainer>
        </section>
      </div>

      <div className="grid two">
        <section className="card">
          <h3>{t('dashboard.weakGroups')}</h3>
          {d.weakGroups.length === 0 ? <Empty text={t('dashboard.noScoredReports')} /> : (
            <table className="table compact">
              <thead><tr><th>#</th><th>{t('dashboard.group')}</th><th className="num">{t('dashboard.avgPercent')}</th><th className="num">n</th></tr></thead>
              <tbody>
                {d.weakGroups.slice(0, 8).map((g) => (
                  <tr key={g.number}>
                    <td>{g.number}</td>
                    <td>{g.name}</td>
                    <td className="num"><span className="bar" style={{ width: `${g.averagePercent}%`, background: g.averagePercent < 71 ? '#e34948' : g.averagePercent < 86 ? '#eda100' : '#008300' }} /> %{g.averagePercent}</td>
                    <td className="num muted">{g.sampleCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="card">
          <h3>{t('dashboard.companies')}</h3>
          <table className="table compact">
            <thead><tr><th>{t('companies.title')}</th><th>{t('dashboard.latest')}</th><th className="num">{t('dashboard.openActions')}</th><th className="num">{t('dashboard.reports')}</th></tr></thead>
            <tbody>
              {d.companyScores.map((c) => (
                <tr key={c.companyId}>
                  <td><Link to={`/reports?companyId=${c.companyId}`}>{c.companyName}</Link></td>
                  <td><GradeBadge grade={c.latestGrade} percent={c.latestPercent} /></td>
                  <td className="num">{c.openActions}</td>
                  <td className="num">{c.auditCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <section className="card">
        <h3>{t('dashboard.recentAudits')}</h3>
        {d.recentAudits.length === 0 ? <Empty text={t('reports.empty')} /> : (
          <table className="table">
            <thead><tr><th>{t('reports.code')}</th><th>{t('reports.title')}</th><th>{t('companies.title')}</th><th>{t('reports.date')}</th><th>{t('reports.status')}</th><th>{t('reports.result')}</th><th className="num">{t('reports.findings')}</th></tr></thead>
            <tbody>
              {d.recentAudits.map((r) => (
                <tr key={r.id}>
                  <td><Link to={`/reports/${r.id}`}>{r.code ?? r.templateCode}</Link></td>
                  <td>{r.title}</td>
                  <td>{r.companyName}</td>
                  <td>{fmtDate(r.auditDate)}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td><GradeBadge grade={r.grade} percent={r.percent} /></td>
                  <td className="num">{r.findingCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
